import { limitsChoiceItems } from 'components/EditOtherDistributionModal/commonData';
import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type {
    WorkOrderChangeCostLimitDistributionData,
    WorkOrderChangeOtherCostObjectItem,
    WorkOrderChangeOtherPriceCostData,
} from 'gql/graphql';
import { OtherCostCalculationTypeEnum } from 'gql/graphql';
import type { TabsIdsDistribution } from 'pages/Project/Common/types';
import { tabItemsDistribution } from 'pages/Project/Common/types';
import { formatNumberToRu } from 'utils';
import { replacerCost } from 'utils/replacerCost';
import { sumFloatNumbers } from 'utils/sumFloatNumbers';

import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import type { FieldErrors, FieldValues, UseFormClearErrors, UseFormReset, UseFormSetError } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { uerUpdateWorkOrderChangeOtherCostGql, workOrderChangeOtherForDistribution } from '../WorkOrderChangeQueries';

interface IUseEditWorkOrderChangeOtherDistribution {
    workOrderChangeId: number;
    workOrderChangeOtherPriceId: number | null;
    workOrderOtherPriceId: number | null;
    setError: UseFormSetError<any>;
    reset: UseFormReset<any>;
    setActiveTab: Dispatch<
        SetStateAction<{
            id: TabsIdsDistribution;
            label: string;
        }>
    >;
    closeEditHandler: () => void;
    refreshOtherTableHandler: () => void;
    handleOpenConfirmationDialog: () => void;
    allFormValues: FieldValues;
    errors: FieldErrors<any>;
    clearErrors: UseFormClearErrors<any>;
    isDirty: boolean;
    defaultObjectCompositeId: string;
}

export const useEditWorkOrderChangeOtherDistribution = ({
    workOrderChangeId,
    workOrderChangeOtherPriceId,
    workOrderOtherPriceId,
    allFormValues,
    reset,
    setError,
    closeEditHandler,
    setActiveTab,
    refreshOtherTableHandler,
    handleOpenConfirmationDialog,
    errors,
    clearErrors,
    isDirty,
    defaultObjectCompositeId,
}: IUseEditWorkOrderChangeOtherDistribution) => {
    const dispatch = useDispatch();

    const distributionVariables = useMemo(
        () => ({ workOrderChangeId, workOrderChangeOtherPriceId, workOrderOtherPriceId }),
        [workOrderChangeId, workOrderChangeOtherPriceId, workOrderOtherPriceId],
    );

    const [distributionData, distributionStatus] = useRequest({
        requestId: 'workOrderChangeDistributionData',
        query: workOrderChangeOtherForDistribution,
        withEffect: !!workOrderChangeId,
        variables: distributionVariables,
    });

    const isDataFetching = useMemo(() => distributionStatus === RequestStatus.FETCHING, [distributionStatus]);
    const { objectsDistributionData, limitsDistributionData, commonDistributionData, seasonsDistributionData } =
        useMemo(
            () => ({
                commonDistributionData:
                    distributionData?.uerWorkOrderChangeOtherForDistribution?.data?.contractOtherCost,
                limitsDistributionData: distributionData?.uerWorkOrderChangeOtherForDistribution?.data?.limits,
                objectsDistributionData: distributionData?.uerWorkOrderChangeOtherForDistribution?.data?.objects,
                seasonsDistributionData:
                    distributionData?.uerWorkOrderChangeOtherForDistribution?.data?.objectSeasonDistributions,
            }),
            [
                distributionData?.uerWorkOrderChangeOtherForDistribution?.data?.contractOtherCost,
                distributionData?.uerWorkOrderChangeOtherForDistribution?.data?.limits,
                distributionData?.uerWorkOrderChangeOtherForDistribution?.data?.objectSeasonDistributions,
                distributionData?.uerWorkOrderChangeOtherForDistribution?.data?.objects,
            ],
        );

    const isCalculating = useMemo(
        () => commonDistributionData?.calculatingType === OtherCostCalculationTypeEnum.Calculation,
        [commonDistributionData?.calculatingType],
    );

    const isFormWithNoCheckedLimits = useMemo(() => {
        if (isCalculating) {
            return Object.values(
                (allFormValues?.includedCost ?? {}) as Record<string, string | null | undefined>,
            ).every((value) => !value || replacerCost(value) === 0);
        }

        return Object.values(
            (allFormValues?.isIncluded ?? {}) as Record<
                string,
                {
                    isIncluded: boolean;
                }
            >,
        ).every((value) => !value?.isIncluded);
    }, [allFormValues, isCalculating]);

    useEffect(() => {
        if (!errors.includedCost) {
            return;
        }
        const currentErrorIncludedCost = Object.keys(errors.includedCost).map((i) => `includedCost.${i}`);

        if (!isFormWithNoCheckedLimits) {
            clearErrors(...currentErrorIncludedCost);
        }
    }, [clearErrors, errors.includedCost, allFormValues, isFormWithNoCheckedLimits]);

    useEffect(() => {
        if (!errors.isIncluded) {
            return;
        }
        const currentErrorIsIncluded = Object.keys(errors.isIncluded).map((i) => `isIncluded.${i}`);

        if (!isFormWithNoCheckedLimits) {
            clearErrors(...currentErrorIsIncluded);
        }
    }, [clearErrors, errors.includedCost, allFormValues, errors.isIncluded, isFormWithNoCheckedLimits]);

    useEffect(() => {
        if (limitsDistributionData) {
            const preparedLimits = limitsDistributionData?.reduce(
                (
                    acc: {
                        costLimit: {
                            [key: number]: number;
                        };
                        totalRemainderCost: {
                            [key: number]: number;
                        };
                        includedCost: {
                            [key: number]: number;
                        };
                        isIncluded: {
                            [key: number]: (typeof limitsChoiceItems)[0];
                        };
                    },
                    item,
                ) => {
                    let isIncluded;

                    if (isCalculating) {
                        isIncluded = item.includedCost ? limitsChoiceItems[0] : limitsChoiceItems[1];
                    } else {
                        isIncluded = item.isIncluded ? limitsChoiceItems[0] : limitsChoiceItems[1];
                    }

                    acc.costLimit[item.season?.id] = formatNumberToRu(item.costLimit, 2);
                    acc.totalRemainderCost[item.season?.id] = formatNumberToRu(item.totalRemainderCost, 2);
                    acc.isIncluded[item.season?.id] = isIncluded;
                    acc.includedCost[item.season?.id] = formatNumberToRu(item.includedCost, 2);

                    return acc;
                },
                { costLimit: {}, totalRemainderCost: {}, includedCost: {}, isIncluded: {} },
            );

            reset({
                costLimit: preparedLimits.costLimit,
                totalRemainderCost: preparedLimits.totalRemainderCost,
                includedCost: preparedLimits.includedCost,
                isIncluded: preparedLimits.isIncluded,
            });
        }
    }, [reset, limitsDistributionData, isCalculating]);

    const linkedSeasonDistributionsData = useMemo<
        | {
              objects: WorkOrderChangeOtherCostObjectItem[] | undefined;
              objectDistributionData: WorkOrderChangeOtherPriceCostData[] | undefined;
              objectLimitsData: WorkOrderChangeCostLimitDistributionData | undefined;
          }[]
        | undefined
    >(
        () =>
            limitsDistributionData?.map((limit) => {
                const currentSeasonId = limit.season?.id;

                const seasonDistributionData = seasonsDistributionData
                    ?.filter((distribution) => distribution?.seasonId === currentSeasonId)
                    .map((o) => ({
                        ...o,
                        isIncluded:
                            defaultObjectCompositeId === `${o.workOrderObjectId}_${o.workOrderChangeObjectId}`
                                ? true
                                : o?.isIncluded,
                    }));

                const currentDistributionObjects = objectsDistributionData?.filter(
                    (object) =>
                        seasonDistributionData?.some((i) => {
                            const objectWorkOrderObjectId = object.rootObjectId.workOrderObjectId;
                            const objectWorkOrderChangeObjectId = object.rootObjectId.workOrderChangeObjectId;

                            return (
                                i.workOrderChangeObjectId === objectWorkOrderChangeObjectId &&
                                i.workOrderObjectId === objectWorkOrderObjectId
                            );
                        }),
                );

                return {
                    objects: currentDistributionObjects,
                    objectDistributionData: seasonDistributionData,
                    objectLimitsData: limit,
                };
            }),
        [defaultObjectCompositeId, limitsDistributionData, objectsDistributionData, seasonsDistributionData],
    );

    const handleCancel = useCallback(() => {
        if (isDirty) {
            handleOpenConfirmationDialog();
        } else {
            closeEditHandler?.();
        }
    }, [closeEditHandler, handleOpenConfirmationDialog, isDirty]);

    const [updateData, updateStatus, , updateRequest] = useRequest({
        requestId: 'updateWorkOrderChangeOtherDistribution',
        query: uerUpdateWorkOrderChangeOtherCostGql,
        withEffect: false,
    });

    const onSubmit = useCallback(
        (formData) => {
            const seasonPrices = Object.entries(formData?.costLimit).reduce(
                (
                    acc: {
                        prices: {
                            seasonId: number;
                            includedCost?: number | null;
                            isIncluded?: boolean;
                            isSystemCalculation: boolean;
                        }[];
                        haveError: boolean;
                    },
                    [key],
                ) => {
                    if (isFormWithNoCheckedLimits) {
                        acc.haveError = true;
                    }

                    const seasonId = Number(key);
                    const seasonIncludedCostValue =
                        !formData.includedCost?.[key] || replacerCost(formData.includedCost?.[key]) === 0
                            ? null
                            : replacerCost(formData.includedCost?.[key]);

                    const seasonObj = {
                        seasonId,
                        ...(isCalculating && {
                            includedCost: seasonIncludedCostValue,
                            isIncluded: Boolean(seasonIncludedCostValue),
                        }),
                        ...(!isCalculating && {
                            isIncluded: allFormValues.isIncluded?.[key]?.isIncluded,
                            includedCost: seasonIncludedCostValue ?? 0,
                        }),
                        isSystemCalculation: formData?.objectDistribution?.find(
                            (d: { seasonId: number }) => d.seasonId === seasonId,
                        ).isSystemCalculation,
                    };

                    acc.prices.push(seasonObj);

                    return acc;
                },
                { prices: [], haveError: false },
            );

            if (seasonPrices.haveError) {
                return;
            }

            const objList = formData?.objectDistribution?.reduce(
                (
                    acc: {
                        objList: Array<{
                            cost: number;
                            objectIds: {
                                workOrderChangeObjectId: number | null;
                                workOrderObjectId: number | null;
                            };
                            seasonId: number;
                        }>;
                        seasonSum: { [key: number]: number };
                        seasonHaveError: boolean;
                    },
                    item: {
                        seasonObjects: Array<{
                            cost: number;
                            isIncluded: boolean;
                            workOrderChangeObjectId: number | null;
                            workOrderObjectId: number | null;
                            seasonId: number;
                            hasErrors: boolean;
                        }>;
                    },
                ) => {
                    item.seasonObjects.forEach((i) => {
                        const itemSeason = i.seasonId;

                        if (i.hasErrors) {
                            acc.seasonHaveError = true;
                        }

                        if (i.isIncluded) {
                            acc.objList.push({
                                cost: replacerCost(i?.cost),
                                objectIds: {
                                    workOrderChangeObjectId: i?.workOrderChangeObjectId,
                                    workOrderObjectId: i?.workOrderObjectId,
                                },
                                seasonId: itemSeason,
                            });

                            acc.seasonSum[itemSeason] = sumFloatNumbers(
                                acc.seasonSum[itemSeason] ?? 0,
                                replacerCost(i.cost ?? 0),
                            );
                        }
                    });

                    return acc;
                },
                {
                    objList: [],
                    seasonSum: {},
                    seasonHaveError: false,
                },
            );

            if (objList?.seasonHaveError) {
                setError('objectDistribution.0.isSystemCalculation', {}, { shouldFocus: true });
                setActiveTab(tabItemsDistribution[3]);

                return;
            }

            /* eslint-disable-next-line */
            for (const price of seasonPrices.prices) {
                const currentSeasonObjSum = objList.seasonSum[price?.seasonId] ? objList.seasonSum[price?.seasonId] : 0;

                if (price.isIncluded && price.includedCost !== currentSeasonObjSum) {
                    setError('objectDistribution.0.isSystemCalculation', {}, { shouldFocus: true });

                    setActiveTab(tabItemsDistribution[3]);

                    return;
                }
                clearErrors('objectDistribution.0.isSystemCalculation');
            }

            const updateVariables = {
                workOrderChangeId,
                workOrderChangeOtherPriceId,
                workOrderOtherPriceId,
                input: {
                    contractOtherId: commonDistributionData?.id,
                    objectList: objList.objList,
                    otherSeasonPrices: seasonPrices.prices,
                },
            };

            dispatch(updateRequest(updateVariables));
        },
        [
            allFormValues.isIncluded,
            clearErrors,
            commonDistributionData?.id,
            dispatch,
            isCalculating,
            isFormWithNoCheckedLimits,
            setActiveTab,
            setError,
            updateRequest,
            workOrderChangeId,
            workOrderChangeOtherPriceId,
            workOrderOtherPriceId,
        ],
    );

    const [resetOtherDistributionData] = useResetStoreData({ requestId: 'workOrderChangeDistributionData' });
    const [resetUpdateCostData] = useResetStoreData({ requestId: 'updateWorkOrderChangeOtherDistribution' });

    const isWorkOrderCostUpdating = useMemo(() => updateStatus === RequestStatus.FETCHING, [updateStatus]);

    useEffect(() => {
        if (!isWorkOrderCostUpdating && updateData?.uerUpdateWorkOrderChangeOtherDistribution?.result) {
            resetOtherDistributionData(undefined);
            resetUpdateCostData(undefined);
            closeEditHandler?.();
            refreshOtherTableHandler();
        }
    }, [
        closeEditHandler,
        isWorkOrderCostUpdating,
        refreshOtherTableHandler,
        resetOtherDistributionData,
        resetUpdateCostData,
        updateData,
    ]);

    return {
        isDataFetching,
        commonDistributionData,
        linkedSeasonDistributionsData,
        isCalculating,
        handleCancel,
        onSubmit,
        limitsDistributionData,
        isWorkOrderCostUpdating,
    };
};
